<template>
  <v-container
    fluid
    class="sla-dashboard pa-4"
  >
    <!-- ═══════════════ HEADER ═══════════════ -->
    <v-card
      flat
      class="header-card mb-4"
      dark
    >
      <v-card-text class="d-flex align-center flex-wrap py-4">
        <div>
          <div class="d-flex align-center">
            <v-icon
              large
              class="mr-3"
            >
              mdi-file-chart
            </v-icon>
            <div>
              <div class="text-h5 font-weight-bold">
                SLA Dashboard
              </div>
              <div class="caption white--text text--lighten-2">
                ORKO × PSO — SLA compliance tracking, breach log &amp; α-factor
                adjusted availability
              </div>
            </div>
          </div>
        </div>
        <v-spacer />
        <v-menu
          v-model="periodMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-model="reportingPeriod"
              label="Reporting Period"
              prepend-inner-icon="mdi-calendar"
              readonly
              dense
              outlined
              hide-details
              dark
              style="max-width: 220px"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="reportingPeriod"
            type="month"
            color="teal"
            @input="periodMenu = false"
          />
        </v-menu>
      </v-card-text>
    </v-card>

    <!-- ═══════════════ KPI SUMMARY CARDS ═══════════════ -->
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        v-for="kpi in kpiCards"
        :key="kpi.label"
        cols="12"
        sm="6"
        md="3"
      >
        <v-card
          outlined
          class="kpi-card"
          :style="{ borderTop: '4px solid ' + kpi.color }"
        >
          <v-card-text class="py-3">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="overline grey--text text--darken-1">
                  {{ kpi.label }}
                </div>
                <div
                  class="text-h4 font-weight-bold"
                  :style="{ color: kpi.color }"
                >
                  {{ kpi.value }}
                </div>
                <div class="caption grey--text">
                  {{ kpi.sub }}
                </div>
              </div>
              <v-avatar
                :color="kpi.color"
                size="44"
                class="lighten-avatar"
              >
                <v-icon dark>
                  {{ kpi.icon }}
                </v-icon>
              </v-avatar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ═══════════════ LEGEND ═══════════════ -->
    <v-card
      outlined
      class="mb-4"
    >
      <v-card-text class="py-2 d-flex align-center flex-wrap caption">
        <span
          class="mr-4"
        ><v-icon
          small
          color="success"
        >mdi-circle</v-icon>
          Green = On Target</span>
        <span
          class="mr-4"
        ><v-icon
          small
          color="warning"
        >mdi-circle</v-icon>
          Amber = At Risk</span>
        <span
          class="mr-4"
        ><v-icon
          small
          color="error"
        >mdi-circle</v-icon>
          Red = Breach</span>
        <v-divider
          vertical
          class="mx-3"
        />
        <span
          class="mr-4"
        ><v-icon
          small
          color="blue"
        >mdi-pencil-box-outline</v-icon>
          Editable input fields</span>
        <span><v-icon
          small
          color="grey darken-3"
        >mdi-calculator</v-icon>
          Auto-calculated</span>
      </v-card-text>
    </v-card>

    <!-- ═══════════════ SECTION TABS ═══════════════ -->
    <v-card outlined>
      <v-tabs
        v-model="activeTab"
        background-color="primary"
        dark
        slider-color="teal accent-3"
        show-arrows
      >
        <v-tab>
          <v-icon
            left
            small
          >
            mdi-clipboard-list-outline
          </v-icon>
          A. Obligation Register
        </v-tab>
        <v-tab>
          <v-icon
            left
            small
          >
            mdi-calendar-check
          </v-icon>
          B. Monthly Tracker
        </v-tab>
        <v-tab>
          <v-icon
            left
            small
          >
            mdi-function-variant
          </v-icon>
          C. α-Factor Availability
        </v-tab>
        <v-tab>
          <v-icon
            left
            small
          >
            mdi-alert-octagon-outline
          </v-icon>
          D. Breach Log
          <v-chip
            v-if="openBreaches"
            x-small
            color="error"
            class="ml-2"
          >
            {{ openBreaches }}
          </v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <!-- ─────────── A. SLA OBLIGATION REGISTER ─────────── -->
        <v-tab-item>
          <v-card-title class="section-title">
            SLA Obligation Register
            <v-spacer />
            <v-text-field
              v-model="registerSearch"
              append-icon="mdi-magnify"
              label="Search SLAs"
              single-line
              hide-details
              dense
              outlined
              style="max-width: 260px"
            />
          </v-card-title>
          <v-data-table
            :headers="registerHeaders"
            :items="slas"
            :search="registerSearch"
            disable-pagination
            hide-default-footer
            class="register-table"
          >
            <template #item.id="{ item }">
              <strong class="primary--text">{{ item.id }}</strong>
            </template>
            <template #item.category="{ item }">
              <v-chip
                x-small
                label
                outlined
                :color="categoryColor(item.category)"
              >
                {{ item.category }}
              </v-chip>
            </template>
            <template #item.thresholds="{ item }">
              <div class="d-flex flex-nowrap">
                <v-chip
                  x-small
                  class="mr-1 white--text"
                  color="success"
                >
                  {{ item.green }}
                </v-chip>
                <v-chip
                  x-small
                  class="mr-1"
                  color="warning"
                >
                  {{ item.amber }}
                </v-chip>
                <v-chip
                  x-small
                  class="white--text"
                  color="error"
                >
                  {{ item.red }}
                </v-chip>
              </div>
            </template>
            <template #item.status="{ item }">
              <v-chip
                small
                :color="item.status === 'Active' ? 'teal' : 'grey'"
                dark
              >
                {{ item.status }}
              </v-chip>
            </template>
          </v-data-table>
        </v-tab-item>

        <!-- ─────────── B. MONTHLY COMPLIANCE TRACKER ─────────── -->
        <v-tab-item>
          <v-card-title class="section-title">
            Monthly SLA Compliance Tracker — {{ trackerYear }}
            <v-spacer />
            <span
              class="caption grey--text mr-2"
            >Enter actuals — status &amp; YTD auto-calculate</span>
          </v-card-title>
          <div class="table-scroll">
            <table class="tracker-table">
              <thead>
                <tr>
                  <th class="text-left sticky-col">
                    SLA / Obligation
                  </th>
                  <th class="text-center">
                    Target
                  </th>
                  <th
                    v-for="m in months"
                    :key="m"
                    class="text-center month-col"
                    colspan="1"
                  >
                    {{ m }}
                  </th>
                  <th class="text-center">
                    YTD Breaches
                  </th>
                  <th class="text-center">
                    YTD Compliance
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="sla in slas"
                  :key="sla.id"
                >
                  <td class="sticky-col">
                    <strong class="primary--text">{{ sla.id }}</strong>
                    <div class="caption grey--text">
                      {{ sla.name }}
                    </div>
                  </td>
                  <td class="text-center font-weight-medium">
                    {{ sla.target }}
                  </td>
                  <td
                    v-for="(m, mi) in months"
                    :key="sla.id + m"
                    class="text-center month-col"
                  >
                    <div class="d-flex align-center justify-center">
                      <v-text-field
                        v-model.number="tracker[sla.id][mi]"
                        dense
                        hide-details
                        outlined
                        type="number"
                        class="month-input"
                        :placeholder="'—'"
                      />
                      <v-tooltip top>
                        <template #activator="{ on }">
                          <v-icon
                            small
                            class="ml-1"
                            :color="statusColor(monthStatus(sla, mi))"
                            v-on="on"
                          >
                            mdi-circle
                          </v-icon>
                        </template>
                        <span>{{ statusLabel(monthStatus(sla, mi)) }}</span>
                      </v-tooltip>
                    </div>
                  </td>
                  <td class="text-center">
                    <v-chip
                      small
                      :color="ytdBreaches(sla) ? 'error' : 'success'"
                      dark
                    >
                      {{ ytdBreaches(sla) }}
                    </v-chip>
                  </td>
                  <td
                    class="text-center font-weight-bold"
                    :class="ytdComplianceClass(sla)"
                  >
                    {{ ytdCompliance(sla) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <v-card-text class="caption grey--text">
            YTD Compliance = 1 − (breaches ÷ months reported). Amber counts as
            "at risk" but not a breach.
          </v-card-text>
        </v-tab-item>

        <!-- ─────────── C. α-FACTOR AVAILABILITY CALCULATOR ─────────── -->
        <v-tab-item>
          <v-card-title class="section-title">
            α-Factor Adjusted Availability Calculator
          </v-card-title>
          <v-card-text>
            <v-alert
              dense
              text
              color="teal"
              border="left"
              class="mb-4"
            >
              <strong>Formula:</strong> Adjusted Availability % = [ (Available
              Hours + α × Excluded Hours) ÷ Total Hours ] × 100
              <br>
              <span class="caption">
                α = weighting factor for excluded downtime (force majeure,
                scheduled maintenance, PSO-side issues)
              </span>
            </v-alert>

            <v-row
              align="center"
              class="mb-2"
            >
              <v-col
                cols="12"
                md="6"
              >
                <div class="d-flex align-center">
                  <span
                    class="mr-4 font-weight-medium"
                  >α factor: {{ alpha.toFixed(2) }}</span>
                  <v-slider
                    v-model="alpha"
                    min="0"
                    max="1"
                    step="0.05"
                    color="teal"
                    track-color="grey lighten-2"
                    hide-details
                    thumb-label
                  />
                </div>
              </v-col>
              <v-col
                cols="6"
                md="3"
              >
                <v-text-field
                  v-model.number="slaTargetPct"
                  label="SLA Target %"
                  type="number"
                  dense
                  outlined
                  hide-details
                  suffix="%"
                />
              </v-col>
              <v-col
                cols="6"
                md="3"
                class="text-right"
              >
                <v-btn
                  color="teal"
                  dark
                  small
                  @click="addStation"
                >
                  <v-icon
                    left
                    small
                  >
                    mdi-plus
                  </v-icon>
                  Add Station
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="alphaHeaders"
              :items="stations"
              disable-pagination
              hide-default-footer
              class="alpha-table"
            >
              <template #item.stationId="{ item }">
                <strong class="primary--text">{{ item.stationId }}</strong>
              </template>
              <template
                v-for="field in [
                  'totalHours',
                  'totalDowntime',
                  'scheduled',
                  'forceMajeure',
                  'psoSide',
                ]"
                #[`item.${field}`]="{ item }"
              >
                <v-text-field
                  :key="field"
                  v-model.number="item[field]"
                  dense
                  hide-details
                  outlined
                  type="number"
                  class="cell-input"
                />
              </template>
              <template #item.excluded="{ item }">
                <span class="calc-cell">{{ excluded(item).toFixed(1) }}</span>
              </template>
              <template #item.unexcused="{ item }">
                <span class="calc-cell">{{ unexcused(item).toFixed(1) }}</span>
              </template>
              <template #item.rawAvail="{ item }">
                <span class="calc-cell">{{ rawAvail(item).toFixed(2) }}%</span>
              </template>
              <template #item.adjAvail="{ item }">
                <strong
                  :class="
                    adjAvail(item) >= slaTargetPct
                      ? 'success--text'
                      : 'error--text'
                  "
                >
                  {{ adjAvail(item).toFixed(2) }}%
                </strong>
              </template>
              <template #item.slaMet="{ item }">
                <v-chip
                  small
                  dark
                  :color="adjAvail(item) >= slaTargetPct ? 'success' : 'error'"
                >
                  <v-icon
                    left
                    x-small
                  >
                    {{
                      adjAvail(item) >= slaTargetPct ? "mdi-check" : "mdi-close"
                    }}
                  </v-icon>
                  {{ adjAvail(item) >= slaTargetPct ? "MET" : "BREACH" }}
                </v-chip>
              </template>
              <template #item.actions="{ item }">
                <v-btn
                  icon
                  x-small
                  @click="removeStation(item)"
                >
                  <v-icon
                    small
                    color="grey"
                  >
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <!-- Fleet summary -->
            <v-row
              dense
              class="mt-4"
            >
              <v-col
                cols="12"
                md="4"
              >
                <v-card
                  outlined
                  class="text-center py-3"
                >
                  <div class="overline grey--text">
                    Fleet Raw Availability
                  </div>
                  <div class="text-h5 font-weight-bold">
                    {{ fleetRaw.toFixed(2) }}%
                  </div>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-card
                  outlined
                  class="text-center py-3"
                >
                  <div class="overline grey--text">
                    Fleet Adjusted Availability
                  </div>
                  <div
                    class="text-h5 font-weight-bold"
                    :class="
                      fleetAdjusted >= slaTargetPct
                        ? 'success--text'
                        : 'error--text'
                    "
                  >
                    {{ fleetAdjusted.toFixed(2) }}%
                  </div>
                </v-card>
              </v-col>
              <v-col
                cols="12"
                md="4"
              >
                <v-card
                  outlined
                  class="text-center py-3"
                >
                  <div class="overline grey--text">
                    Stations in Breach
                  </div>
                  <div class="text-h5 font-weight-bold error--text">
                    {{ stationsInBreach }}
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-tab-item>

        <!-- ─────────── D. BREACH & ESCALATION LOG ─────────── -->
        <v-tab-item>
          <v-card-title class="section-title">
            SLA Breach &amp; Escalation Log
            <v-spacer />
            <v-btn
              color="error"
              dark
              small
              @click="openBreachDialog()"
            >
              <v-icon
                left
                small
              >
                mdi-plus
              </v-icon>
              Log Breach
            </v-btn>
          </v-card-title>
          <v-data-table
            :headers="breachHeaders"
            :items="breaches"
            disable-pagination
            hide-default-footer
            no-data-text="No breaches logged — the network is fully compliant 🎉"
          >
            <template #item.breachId="{ item }">
              <strong class="error--text">{{ item.breachId }}</strong>
            </template>
            <template #item.severity="{ item }">
              <v-chip
                x-small
                dark
                :color="severityColor(item.severity)"
              >
                {{ item.severity }}
              </v-chip>
            </template>
            <template #item.rcaSubmitted="{ item }">
              <v-icon
                small
                :color="item.rcaSubmitted ? 'success' : 'grey'"
              >
                {{
                  item.rcaSubmitted
                    ? "mdi-check-circle"
                    : "mdi-minus-circle-outline"
                }}
              </v-icon>
            </template>
            <template #item.status="{ item }">
              <v-chip
                x-small
                dark
                :color="
                  item.status === 'Closed'
                    ? 'success'
                    : item.status === 'In Progress'
                      ? 'warning'
                      : 'error'
                "
              >
                {{ item.status }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-btn
                icon
                x-small
                @click="openBreachDialog(item)"
              >
                <v-icon
                  small
                  color="teal"
                >
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="deleteBreach(item)"
              >
                <v-icon
                  small
                  color="grey"
                >
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- ═══════════════ BREACH DIALOG ═══════════════ -->
    <v-dialog
      v-model="breachDialog"
      max-width="720"
      persistent
    >
      <v-card>
        <v-card-title class="primary white--text">
          {{ editingBreach ? "Edit Breach" : "Log New Breach" }}
          <v-spacer />
          <v-btn
            icon
            dark
            @click="breachDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-form
            ref="breachForm"
            v-model="breachFormValid"
          >
            <v-row dense>
              <v-col
                cols="12"
                sm="4"
              >
                <v-select
                  v-model="breachForm.slaId"
                  :items="slas.map((s) => s.id)"
                  label="SLA ID *"
                  dense
                  outlined
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-select
                  v-model="breachForm.station"
                  :items="stations.map((s) => s.stationId)"
                  label="Station *"
                  dense
                  outlined
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="breachForm.date"
                  label="Date *"
                  type="date"
                  dense
                  outlined
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="breachForm.description"
                  label="Description *"
                  dense
                  outlined
                  :rules="[(v) => !!v || 'Required']"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-select
                  v-model="breachForm.severity"
                  :items="['P1', 'P2', 'P3']"
                  label="Severity"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="breachForm.duration"
                  label="Duration of Breach"
                  dense
                  outlined
                  placeholder="e.g. 6 hrs"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-select
                  v-model="breachForm.status"
                  :items="['Open', 'In Progress', 'Closed']"
                  label="Status"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="breachForm.rootCause"
                  label="Root Cause"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="breachForm.customerImpact"
                  label="Customer Impact"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="breachForm.immediateAction"
                  label="Immediate Action"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
              >
                <v-text-field
                  v-model="breachForm.resolution"
                  label="Resolution"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="breachForm.closedDate"
                  label="Closed Date"
                  type="date"
                  dense
                  outlined
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-checkbox
                  v-model="breachForm.rcaSubmitted"
                  label="RCA Submitted?"
                  dense
                  class="mt-1"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
              >
                <v-text-field
                  v-model="breachForm.approvedBy"
                  label="Approved By"
                  dense
                  outlined
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="breachDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            dark
            :disabled="!breachFormValid"
            @click="saveBreach"
          >
            {{ editingBreach ? "Save Changes" : "Log Breach" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: "SlaDashboard",

  data() {
    return {
      activeTab: 0,
      periodMenu: false,
      reportingPeriod: "2026-06",
      registerSearch: "",
      trackerYear: 2026,
      months: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],

      // ── A. SLA Obligation Register (from Excel rows 6–13) ──
      // dir: 'min' = higher is better (≥), 'max' = lower is better (≤)
      slas: [
        {
          id: "SLA-01",
          name: "Network Availability",
          category: "Availability",
          metric: "% uptime",
          target: "≥ 98%",
          method: "OCPP logs",
          frequency: "Monthly",
          grace: "None",
          green: "≥98%",
          amber: "95–98%",
          red: "<95%",
          review: "Monthly",
          status: "Active",
          dir: "min",
          greenBound: 98,
          amberBound: 95,
        },
        {
          id: "SLA-02",
          name: "MTTR for P1 Faults",
          category: "Reliability",
          metric: "Hours to repair",
          target: "≤ 4 hrs",
          method: "Ticketing",
          frequency: "Per event",
          grace: "None",
          green: "≤4h",
          amber: "4–8h",
          red: ">8h",
          review: "Monthly",
          status: "Active",
          dir: "max",
          greenBound: 4,
          amberBound: 8,
        },
        {
          id: "SLA-03",
          name: "First-Time Charge Success",
          category: "Quality",
          metric: "% success",
          target: "≥ 95%",
          method: "OCPP transaction log",
          frequency: "Monthly",
          grace: "None",
          green: "≥95%",
          amber: "90–95%",
          red: "<90%",
          review: "Monthly",
          status: "Active",
          dir: "min",
          greenBound: 95,
          amberBound: 90,
        },
        {
          id: "SLA-04",
          name: "P1 Incident Response",
          category: "Reliability",
          metric: "Minutes",
          target: "≤ 60 min",
          method: "Ticketing",
          frequency: "Per event",
          grace: "5 min",
          green: "≤60m",
          amber: "60–120m",
          red: ">120m",
          review: "Monthly",
          status: "Active",
          dir: "max",
          greenBound: 60,
          amberBound: 120,
        },
        {
          id: "SLA-05",
          name: "Monthly Report Delivery",
          category: "Compliance",
          metric: "Days post month-end",
          target: "≤ 5 days",
          method: "Report timestamp",
          frequency: "Monthly",
          grace: "None",
          green: "≤5d",
          amber: "5–7d",
          red: ">7d",
          review: "Monthly",
          status: "Active",
          dir: "max",
          greenBound: 5,
          amberBound: 7,
        },
        {
          id: "SLA-06",
          name: "PSO Fuel Card Uptime",
          category: "Integration",
          metric: "% card acceptance",
          target: "≥ 95%",
          method: "Payment gateway",
          frequency: "Monthly",
          grace: "None",
          green: "≥95%",
          amber: "90–95%",
          red: "<90%",
          review: "Monthly",
          status: "Active",
          dir: "min",
          greenBound: 95,
          amberBound: 90,
        },
        {
          id: "SLA-07",
          name: "Preventive Maintenance",
          category: "Maintenance",
          metric: "% tasks on schedule",
          target: "100%",
          method: "CMMS",
          frequency: "Quarterly",
          grace: "None",
          green: "100%",
          amber: "90–100%",
          red: "<90%",
          review: "Quarterly",
          status: "Active",
          dir: "min",
          greenBound: 100,
          amberBound: 90,
        },
        {
          id: "SLA-08",
          name: "Software/CSMS Uptime",
          category: "Technical",
          metric: "% platform uptime",
          target: "≥ 99.5%",
          method: "GCP Monitoring",
          frequency: "Monthly",
          grace: "None",
          green: "≥99.5%",
          amber: "98–99.5%",
          red: "<98%",
          review: "Monthly",
          status: "Active",
          dir: "min",
          greenBound: 99.5,
          amberBound: 98,
        },
      ],

      // ── B. Tracker: actual values per SLA per month (null = not reported) ──
      tracker: {
        "SLA-01": [98.6, 97.2, 99.1, null, null, null],
        "SLA-02": [3.5, 4.5, 2.8, null, null, null],
        "SLA-03": [96.4, 95.8, 94.2, null, null, null],
        "SLA-04": [45, 70, 130, null, null, null],
        "SLA-05": [3, 4, 6, null, null, null],
        "SLA-06": [97.1, 96.3, 95.5, null, null, null],
        "SLA-07": [100, 100, 92, null, null, null],
        "SLA-08": [99.8, 99.6, 99.9, null, null, null],
      },

      // ── C. α-factor calculator (from Excel rows 31–35) ──
      alpha: 1.0,
      slaTargetPct: 98,
      stations: [
        {
          stationId: "PSO-LHR-01",
          period: "Jun-2026",
          totalHours: 720,
          totalDowntime: 12,
          scheduled: 6,
          forceMajeure: 0,
          psoSide: 2,
        },
        {
          stationId: "PSO-LHR-02",
          period: "Jun-2026",
          totalHours: 720,
          totalDowntime: 30,
          scheduled: 4,
          forceMajeure: 0,
          psoSide: 0,
        },
        {
          stationId: "PSO-ISB-01",
          period: "Jun-2026",
          totalHours: 720,
          totalDowntime: 5,
          scheduled: 5,
          forceMajeure: 0,
          psoSide: 0,
        },
        {
          stationId: "PSO-ISB-02",
          period: "Jun-2026",
          totalHours: 720,
          totalDowntime: 0,
          scheduled: 0,
          forceMajeure: 0,
          psoSide: 0,
        },
        {
          stationId: "PSO-KHI-01",
          period: "Jun-2026",
          totalHours: 720,
          totalDowntime: 48,
          scheduled: 8,
          forceMajeure: 24,
          psoSide: 0,
        },
      ],

      // ── D. Breach log ──
      breaches: [
        {
          breachId: "BREACH-0001",
          slaId: "SLA-04",
          station: "PSO-LHR-02",
          date: "2026-03-14",
          description:
            "P1 response exceeded 120 min due to on-call escalation gap",
          severity: "P1",
          rootCause: "Escalation matrix outdated",
          duration: "2.2 hrs",
          customerImpact: "Charger offline, 14 failed sessions",
          immediateAction: "Manual reboot via CSMS",
          resolution: "Escalation matrix updated in ticketing tool",
          closedDate: "2026-03-20",
          rcaSubmitted: true,
          approvedBy: "Ops Manager",
          status: "Closed",
        },
      ],
      breachDialog: false,
      breachFormValid: false,
      editingBreach: null,
      breachForm: this.emptyBreach(),

      // ── Table headers ──
      registerHeaders: [
        { text: "SLA ID", value: "id", width: 90 },
        { text: "Obligation", value: "name" },
        { text: "Category", value: "category" },
        { text: "Metric", value: "metric" },
        { text: "Target", value: "target" },
        { text: "Measurement", value: "method" },
        { text: "Frequency", value: "frequency" },
        { text: "Grace", value: "grace" },
        {
          text: "Thresholds (G / A / R)",
          value: "thresholds",
          sortable: false,
          width: 220,
        },
        { text: "Review", value: "review" },
        { text: "Status", value: "status" },
      ],
      alphaHeaders: [
        { text: "Station ID", value: "stationId", width: 130 },
        { text: "Total Hrs", value: "totalHours", width: 100 },
        { text: "Downtime (hrs)", value: "totalDowntime", width: 110 },
        { text: "Scheduled (hrs)", value: "scheduled", width: 110 },
        { text: "Force Majeure (hrs)", value: "forceMajeure", width: 120 },
        { text: "PSO-Side (hrs)", value: "psoSide", width: 110 },
        { text: "Excluded", value: "excluded", width: 90 },
        { text: "Unexcused", value: "unexcused", width: 95 },
        { text: "Raw %", value: "rawAvail", width: 90 },
        { text: "Adjusted %", value: "adjAvail", width: 100 },
        { text: "SLA Met?", value: "slaMet", width: 110 },
        { text: "", value: "actions", sortable: false, width: 50 },
      ],
      breachHeaders: [
        { text: "Breach ID", value: "breachId", width: 120 },
        { text: "SLA", value: "slaId", width: 90 },
        { text: "Station", value: "station" },
        { text: "Date", value: "date" },
        { text: "Description", value: "description" },
        { text: "Severity", value: "severity" },
        { text: "Root Cause", value: "rootCause" },
        { text: "Duration", value: "duration" },
        { text: "RCA", value: "rcaSubmitted", align: "center" },
        { text: "Status", value: "status" },
        { text: "", value: "actions", sortable: false, width: 90 },
      ],
    };
  },

  computed: {
    openBreaches() {
      return this.breaches.filter((b) => b.status !== "Closed").length;
    },
    totalYtdBreaches() {
      return this.slas.reduce((sum, sla) => sum + this.ytdBreaches(sla), 0);
    },
    atRiskCount() {
      // SLAs whose latest reported month is amber
      return this.slas.filter((sla) => {
        const vals = this.tracker[sla.id];
        for (let i = vals.length - 1; i >= 0; i--) {
          if (vals[i] !== null && vals[i] !== "" && vals[i] !== undefined) {
            return this.evaluate(sla, vals[i]) === "amber";
          }
        }
        return false;
      }).length;
    },
    overallCompliance() {
      let reported = 0;
      let breached = 0;
      this.slas.forEach((sla) => {
        this.tracker[sla.id].forEach((v) => {
          if (v !== null && v !== "" && v !== undefined) {
            reported++;
            if (this.evaluate(sla, v) === "red") breached++;
          }
        });
      });
      if (!reported) return "—";
      return ((1 - breached / reported) * 100).toFixed(1) + "%";
    },
    kpiCards() {
      return [
        {
          label: "Overall Compliance",
          value: this.overallCompliance,
          sub: "YTD, all SLAs",
          icon: "mdi-shield-check",
          color: "#1B5E20",
        },
        {
          label: "Active SLAs",
          value: this.slas.filter((s) => s.status === "Active").length,
          sub: "Under agreement",
          icon: "mdi-clipboard-list",
          color: "#00897B",
        },
        {
          label: "YTD Breaches",
          value: this.totalYtdBreaches,
          sub: "Red statuses recorded",
          icon: "mdi-alert-octagon",
          color: "#C62828",
        },
        {
          label: "At Risk (Amber)",
          value: this.atRiskCount,
          sub: "Latest reported month",
          icon: "mdi-alert",
          color: "#F9A825",
        },
      ];
    },
    fleetRaw() {
      const total = this.stations.reduce((s, x) => s + (x.totalHours || 0), 0);
      const avail = this.stations.reduce(
        (s, x) => s + this.availableHours(x),
        0
      );
      return total ? (avail / total) * 100 : 0;
    },
    fleetAdjusted() {
      const total = this.stations.reduce((s, x) => s + (x.totalHours || 0), 0);
      const adj = this.stations.reduce(
        (s, x) => s + this.availableHours(x) + this.alpha * this.excluded(x),
        0
      );
      return total ? Math.min((adj / total) * 100, 100) : 0;
    },
    stationsInBreach() {
      return this.stations.filter((s) => this.adjAvail(s) < this.slaTargetPct)
        .length;
    },
  },

  mounted() {
    const params = {
      date: ["2026-07-05", "2026-07-05"],
    };
    this.$store.dispatch("slaDashboard/list", params);
  },

  methods: {
    // ── Status evaluation ──
    evaluate(sla, value) {
      if (value === null || value === "" || value === undefined || isNaN(value))
        return "none";
      const v = Number(value);
      if (sla.dir === "min") {
        if (v >= sla.greenBound) return "green";
        if (v >= sla.amberBound) return "amber";
        return "red";
      }
      // dir === 'max' → lower is better
      if (v <= sla.greenBound) return "green";
      if (v <= sla.amberBound) return "amber";
      return "red";
    },
    monthStatus(sla, monthIndex) {
      return this.evaluate(sla, this.tracker[sla.id][monthIndex]);
    },
    statusColor(status) {
      return {
        green: "success",
        amber: "warning",
        red: "error",
        none: "grey lighten-1",
      }[status];
    },
    statusLabel(status) {
      return {
        green: "On Target",
        amber: "At Risk",
        red: "Breach",
        none: "Not reported",
      }[status];
    },
    ytdBreaches(sla) {
      return this.tracker[sla.id].filter((v) => this.evaluate(sla, v) === "red")
        .length;
    },
    ytdCompliance(sla) {
      const reported = this.tracker[sla.id].filter(
        (v) => v !== null && v !== "" && v !== undefined
      ).length;
      if (!reported) return "—";
      return ((1 - this.ytdBreaches(sla) / reported) * 100).toFixed(0) + "%";
    },
    ytdComplianceClass(sla) {
      const b = this.ytdBreaches(sla);
      return b === 0
        ? "success--text"
        : b === 1
        ? "warning--text"
        : "error--text";
    },
    categoryColor(cat) {
      return (
        {
          Availability: "green darken-2",
          Reliability: "blue darken-1",
          Quality: "purple",
          Compliance: "brown",
          Integration: "indigo",
          Maintenance: "orange darken-2",
          Technical: "teal darken-1",
        }[cat] || "grey"
      );
    },

    // ── α-factor math ──
    excluded(s) {
      return (s.scheduled || 0) + (s.forceMajeure || 0) + (s.psoSide || 0);
    },
    unexcused(s) {
      return Math.max((s.totalDowntime || 0) - this.excluded(s), 0);
    },
    availableHours(s) {
      return Math.max((s.totalHours || 0) - (s.totalDowntime || 0), 0);
    },
    rawAvail(s) {
      return s.totalHours ? (this.availableHours(s) / s.totalHours) * 100 : 0;
    },
    adjAvail(s) {
      if (!s.totalHours) return 0;
      const adj =
        ((this.availableHours(s) + this.alpha * this.excluded(s)) /
          s.totalHours) *
        100;
      return Math.min(adj, 100);
    },
    addStation() {
      this.stations.push({
        stationId: `PSO-NEW-${String(this.stations.length + 1).padStart(
          2,
          "0"
        )}`,
        period: this.reportingPeriod,
        totalHours: 720,
        totalDowntime: 0,
        scheduled: 0,
        forceMajeure: 0,
        psoSide: 0,
      });
    },
    removeStation(item) {
      this.stations = this.stations.filter((s) => s !== item);
    },

    // ── Breach log ──
    emptyBreach() {
      return {
        slaId: "",
        station: "",
        date: "",
        description: "",
        severity: "P2",
        rootCause: "",
        duration: "",
        customerImpact: "",
        immediateAction: "",
        resolution: "",
        closedDate: "",
        rcaSubmitted: false,
        approvedBy: "",
        status: "Open",
      };
    },
    openBreachDialog(item = null) {
      this.editingBreach = item;
      this.breachForm = item ? { ...item } : this.emptyBreach();
      this.breachDialog = true;
      this.$nextTick(
        () => this.$refs.breachForm && this.$refs.breachForm.resetValidation()
      );
    },
    saveBreach() {
      if (this.editingBreach) {
        Object.assign(this.editingBreach, this.breachForm);
      } else {
        const id = `BREACH-${String(this.breaches.length + 1).padStart(
          4,
          "0"
        )}`;
        this.breaches.push({ ...this.breachForm, breachId: id });
      }
      this.breachDialog = false;
    },
    deleteBreach(item) {
      this.breaches = this.breaches.filter((b) => b !== item);
    },
    severityColor(sev) {
      return (
        { P1: "error", P2: "orange darken-2", P3: "blue-grey" }[sev] || "grey"
      );
    },
  },
};
</script>

<style scoped>
.sla-dashboard {
  background: #f4f6f5;
  min-height: 100vh;
}

/* Header — ORKO deep forest green */
.header-card {
  background: linear-gradient(
    135deg,
    #0e2f25 0%,
    #1b4332 60%,
    #14532d 100%
  ) !important;
  border-radius: 10px;
}

.kpi-card {
  border-radius: 8px;
  background: #fff;
}
.lighten-avatar {
  opacity: 0.92;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1b4332;
  border-bottom: 1px solid #e0e0e0;
}

/* Monthly tracker table */
.table-scroll {
  overflow-x: auto;
}
.tracker-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1100px;
}
.tracker-table th {
  background: #1b4332;
  color: #fff;
  padding: 10px 8px;
  font-weight: 600;
  white-space: nowrap;
}
.tracker-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eceff1;
}
.tracker-table tbody tr:hover {
  background: #f1f8f5;
}
.sticky-col {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 1;
  min-width: 220px;
}
.tracker-table th.sticky-col {
  background: #1b4332;
  z-index: 2;
}
.month-col {
  min-width: 110px;
}
.month-input {
  max-width: 72px;
}
.month-input >>> input {
  text-align: center;
  font-size: 13px;
  padding: 4px 0;
}
.month-input >>> .v-input__slot {
  min-height: 30px !important;
  background: #e8f4fd !important; /* blue = input field, matching the sheet legend */
}

/* α table */
.cell-input {
  max-width: 90px;
}
.cell-input >>> input {
  text-align: center;
  font-size: 13px;
}
.cell-input >>> .v-input__slot {
  min-height: 30px !important;
  background: #e8f4fd !important;
}
.calc-cell {
  font-family: "Roboto Mono", monospace;
  font-size: 13px;
  color: #263238;
}

.register-table >>> th {
  background: #f1f8f5 !important;
  white-space: nowrap;
}
.alpha-table >>> th {
  background: #f1f8f5 !important;
  white-space: nowrap;
}
</style>
